import Button from "@/common/components/Button";
import Modal from "@/common/components/Modal";
import { useState } from "react";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { useQueryClient } from "@tanstack/react-query";
import { SettingsIcon } from "lucide-react";
import { formatDateTime } from "@/common/utils/formatters";
import { PaymentData } from "../../types/payment.type";
import { useBillingPaymentCancel } from "../../hooks/useBillingPaymentCancel";
import { useBillingPaymentUncancel } from "../../hooks/useBillingPaymentUncancel";
import { useBillingPaymentMarkAsPaid } from "../../hooks/useBillingPaymentMarkAsPaid";
import { useBillingPaymentMarkAsUnpaid } from "../../hooks/useBillingPaymentMarkAsUnpaid";
import { billPaymentType } from "../../constants/billPaymentType.constant";

interface PaymentChangeStatusModalProps {
  payment: PaymentData;
}

const PaymentChangeStatusModal = ({ payment }: PaymentChangeStatusModalProps) => {
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);

  const [isError, setIsError] = useState<boolean>(false);
  const [messages, setMessages] = useState<string[] | boolean>(false);

  const { mutate: cancelPayment } = useBillingPaymentCancel({
    paymentId: payment.id,
    onSuccess: () => {
      setIsError(false);
      setMessages(['Cobrança cancelada com sucesso!']);
    },
    onError: () => {
      setIsError(true);
      setMessages(['Ocorreu um erro ao cancelar a cobrança.']);
    },
  });

  const { mutate: uncancelPayment } = useBillingPaymentUncancel({
    paymentId: payment.id,
    onSuccess: () => {
      setIsError(false);
      setMessages(['Cobrança descancelada com sucesso!']);
    },
    onError: () => {
      setIsError(true);
      setMessages(['Ocorreu um erro ao descancelar a cobrança.']);
    },
  });

  const { mutate: markAsPaidPayment } = useBillingPaymentMarkAsPaid({
    paymentId: payment.id,
    onSuccess: () => {
      setIsError(false);
      setMessages(['Cobrança marcada como paga com sucesso!']);
    },
    onError: () => {
      setIsError(true);
      setMessages(['Ocorreu um erro ao marcar cobrança como paga.']);
    },
  });

  const { mutate: markAsUnpaidPayment } = useBillingPaymentMarkAsUnpaid({
    paymentId: payment.id,
    onSuccess: () => {
      setIsError(false);
      setMessages(['Cobrança marcada como não paga com sucesso!']);
    },
    onError: () => {
      setIsError(true);
      setMessages(['Ocorreu um erro ao marcar cobrança como não paga.']);
    },
  });

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        onConfirm={() => setIsOpen(false)}
        Trigger={<SettingsIcon strokeWidth={1} />}
        title="Alterar status de cobrança"
        footer="Fechar"
        body={(
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-3" data-testid="payments_fields_section">
              <div>
                <p className="text-[14px] font-light">Tipo</p>
                <p className="text-[16px] text-2xl text-muted">{billPaymentType[payment.type as keyof typeof billPaymentType] || payment.type || '--'}</p>
              </div>

              <div>
                <p className="text-[14px] font-light">Autor</p>
                <p className="text-[16px] text-2xl text-muted">{payment.uploaded_by?.full_name || '--'}</p>
              </div>

              <div>
                <p className="text-[14px] font-light">Número do título</p>
                <p className="text-[16px] text-2xl text-muted">{payment.data?.title_number || '--'}</p>
              </div>

              <div>
                <p className="text-[14px] font-light">Data de emissão</p>
                <p className="text-[16px] text-2xl text-muted">{payment.data?.issued_at ? formatDateTime(new Date(payment.data.issued_at)) : '--'}</p>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              {payment.canceled_at ? (
                <>
                  <p className="text-white-800">Cobrança cancelada em {formatDateTime(new Date(payment.canceled_at))}</p>

                  <Button
                    onClick={() => uncancelPayment()}
                    variant="yellow"
                    className="text-white-800"
                  >
                    Descancelar cobrança
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => cancelPayment()}
                  variant="error"
                  className="text-white-800"
                >
                  Cancelar cobrança
                </Button>
              )}
            </div>

            <div className="flex flex-col gap-3">
              {payment.paid_at ? (
                <>
                  <p className="text-white-800">Cobrança marcada como paga em {formatDateTime(new Date(payment.paid_at))}</p>

                  <Button
                    onClick={() => markAsUnpaidPayment()}
                    variant="error"
                    className="text-white-800"
                  >
                    Marcar cobrança como não paga
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => markAsPaidPayment()}
                  variant="yellow"
                  className="text-white-800"
                >
                  Marcar cobrança como paga
                </Button>
              )}
            </div>
          </div>
        )}
      />

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError={isError}
          onClose={() => {
            queryClient.invalidateQueries({ queryKey: ['billingBillPayments'] });
          }}
        />
      )}
    </>
  );
};

export default PaymentChangeStatusModal;
